import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useRefresh, triggerRefresh } from "@/hooks/useRefresh";
import { peso, UNITS, CATEGORIES } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Plus, Pencil } from "lucide-react";

const EMPTY = { name: "", category: "Meat", unit: "kg", unit_cost: "", vat_inclusive: true, reorder_point: "", sku: "" };

export default function Items() {
  const { auth } = useAuth();
  const role = auth?.membership?.role;
  const canEdit = role === "owner" || role === "manager";
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  useRefresh(() => api.get("/items").then((r) => setItems(r.data)), []);

  const openNew = () => { setForm(EMPTY); setEditing("new"); };
  const openEdit = (i) => { setForm({ ...i, unit_cost: String(i.unit_cost), reorder_point: String(i.reorder_point) }); setEditing(i.item_id); };

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form, unit_cost: parseFloat(form.unit_cost || 0), reorder_point: parseFloat(form.reorder_point || 0) };
    try {
      if (editing === "new") await api.post("/items", payload);
      else await api.put(`/items/${editing}`, payload);
      toast.success("Item saved");
      setEditing(null);
      triggerRefresh();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const filtered = items.filter((i) => (i.name + i.category + (i.sku || "")).toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-4" data-testid="items-page">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Items</h1>
        <div className="flex gap-2">
          <Input placeholder="Search items…" className="w-48 sm:w-64" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="items-search-input" />
          {canEdit && <Button onClick={openNew} data-testid="add-item-btn"><Plus className="h-4 w-4 mr-1" /> Add item</Button>}
        </div>
      </div>
      <div className="border rounded-xl bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wider text-muted-foreground text-left">
            <tr className="border-b">
              <th className="py-2 px-3">Item</th><th className="py-2 px-3">SKU</th><th className="py-2 px-3">Category</th>
              <th className="py-2 px-3">Unit</th><th className="py-2 px-3 text-right">Unit cost</th>
              <th className="py-2 px-3">VAT</th><th className="py-2 px-3 text-right">Reorder pt</th>
              {canEdit && <th className="py-2 px-3"></th>}
            </tr>
          </thead>
          <tbody>
            {filtered.map((i) => (
              <tr key={i.item_id} className="border-b last:border-0 hover:bg-slate-50" data-testid={`item-row-${i.item_id}`}>
                <td className="py-2 px-3 font-medium">{i.name}</td>
                <td className="py-2 px-3 text-muted-foreground">{i.sku}</td>
                <td className="py-2 px-3">{i.category}</td>
                <td className="py-2 px-3">{i.unit}</td>
                <td className="py-2 px-3 text-right">{peso(i.unit_cost)}</td>
                <td className="py-2 px-3 text-xs">{i.vat_inclusive ? "VAT-in" : "VAT-ex"}</td>
                <td className="py-2 px-3 text-right">{i.reorder_point}</td>
                {canEdit && (
                  <td className="py-2 px-3 text-right">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(i)} data-testid={`edit-item-${i.item_id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="sm:max-w-md" data-testid="item-dialog">
          <DialogHeader><DialogTitle>{editing === "new" ? "Add item" : "Edit item"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="space-y-2"><Label>Name</Label>
              <Input data-testid="item-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="item-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="space-y-2"><Label>Unit</Label>
                <Select value={form.unit} onValueChange={(v) => setForm({ ...form, unit: v })}>
                  <SelectTrigger data-testid="item-unit-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Unit cost (₱)</Label>
                <Input type="number" step="any" min="0" data-testid="item-cost-input" value={form.unit_cost} onChange={(e) => setForm({ ...form, unit_cost: e.target.value })} required /></div>
              <div className="space-y-2"><Label>Reorder point</Label>
                <Input type="number" step="any" min="0" data-testid="item-reorder-input" value={form.reorder_point} onChange={(e) => setForm({ ...form, reorder_point: e.target.value })} /></div>
            </div>
            <div className="space-y-2"><Label>SKU (optional)</Label>
              <Input data-testid="item-sku-input" value={form.sku || ""} onChange={(e) => setForm({ ...form, sku: e.target.value })} /></div>
            <div className="flex items-center justify-between border rounded-md px-3 py-2">
              <Label className="text-sm">Cost is VAT-inclusive</Label>
              <Switch checked={form.vat_inclusive} onCheckedChange={(v) => setForm({ ...form, vat_inclusive: v })} data-testid="item-vat-switch" />
            </div>
            <Button type="submit" className="w-full" data-testid="item-save-btn">Save item</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
